import { Injectable } from '@angular/core';
import { LoginService } from '../services/login.service';
import { AuthClaims, AuthRoles, AuthUserType } from './auth.types';
import { debounceTime } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { VirtualGroupRelation } from '../shared/app.enums';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  claims: Array<AuthClaims>; // Store user claims
  roles: Array<AuthRoles>; // Store user roles
  userType: AuthUserType; // Store user type
  authOrgCode: string; // Store user's organization
  linkedOrgCodes: string[] = []; // Store organizations linked to user's organization
  cdsVirtualGroups: ServerModels.VirtualGroupViewModel[];
  organizationId: string;
  currentUserId: string;
  currentUserName: string;
  protected apiRoot: string = environment.apiEndpoint;

  constructor(private loginService: LoginService,
    protected http: HttpClient
  ) { }

  clearClaims() {
    this.claims = null;
  }

  clearRoles() {
    this.roles = null;
  }

  clearUserType() {
    this.userType = null;
  }

  clearAuthOrgCode() {
    this.authOrgCode = null;
  }

  hasRole(authRoles: AuthRoles[]): boolean {
    if (!authRoles || !authRoles.length)
      return false;

    if (this.roles) {
      for (let i = 0; i < authRoles.length; i++) {
        if (this.roles.find(role => { return authRoles[i] === role; })) {
          return true;
        }
      }
    }

    return false;
  }

  hasUserType(userType: AuthUserType[]): boolean {
    if (!this.userType || !userType) {
      return false;
    }
    for (let i = 0; i < userType.length; i++) {
      if (userType.find(type => { return type === this.userType; })) {
        return true;
      }
    }

    return false;
  }

  hasUserTypeRole(perms: { roles: AuthRoles[], userType: AuthUserType }[]): boolean {
    if (perms == null || !perms.length) {
      return false;
    }

    for (let i = 0; i < perms.length; i++) {
      if (this.hasUserType([perms[i].userType])) {
        if (this.hasRole(perms[i].roles)) {
          return true;
        }
      }
    }

    return false;
  }

  hasOrgCode(orgCode: string): boolean {
    if (!orgCode) {
      return false;
    }

    let hasOrg: boolean = this.authOrgCode == orgCode;
    let hasLinkedOrg: boolean = this.linkedOrgCodes.findIndex(x => x == orgCode) > -1;
    return hasOrg || hasLinkedOrg;
  }

  hasClaim(authClaims: AuthClaims[]) {
    if (this.claims) {
      for (let i = 0; i < authClaims.length; i++) {
        if (this.claims.find(claim => { return authClaims[i] === claim['m_type']; })) {
          return true;
        }
      }
    }
    return false;
  }

  isCreator(userId: string): boolean {
    return !!userId && (this.currentUserId?.toLowerCase() == userId.toLowerCase() || this.currentUserName?.toLowerCase() == userId.toLowerCase());
  }

  isSupervisor(supervisorId: string): boolean {
    return this.currentUserId?.toLowerCase() == supervisorId?.toLowerCase();
  }

  // Added for defects related to S-18146 so HQ Admins and Power Users can edit any result
  isHQAdminOrPowerUser(): boolean {
    if (this.userType === "ATFHQ") {
      if (this.hasRole(['Application Administrator', 'Power User'])) {
        return true;
      }
      return false;
    }
    return false;
  }

  isCDSApprover(): boolean {
    return this.userType === 'StateLocalLE' && this.hasRole(['CDS Approver']);
  }

  isCDSGuest(): boolean {
    if (this.userType == 'StateLocalLE') {
      return this.cdsVirtualGroups.length > 0;
    }

    return false;
  }

  // This method is called once and a list of roles is stored in the roles property
  initializeRoles() {
    this.clearRoles();
    this.clearUserType();
    this.clearAuthOrgCode();
    return new Promise<void>((resolve, reject) => {
      // Call API to retrieve the list of actions this user is permitted to perform.
      this.loginService.getUserInfo().pipe(debounceTime(3000)).subscribe({
        next: (info: ServerModels.UserInfoViewModel) => {
          this.roles = info['Roles'].map(x => { return x.Name as AuthRoles });
          this.userType = info['UserType']['UserTypeName'] as AuthUserType;
          if (this.userType == 'ATFFieldDivision') {
            this.authOrgCode = info['FieldDivision']['FieldDivisionID'];
          }
          if (this.userType == 'StateLocalLE') {
            this.authOrgCode = info['Agency']['AgencyID'];
            this.organizationId = info['Agency']['ENESS_REF_Organization_ID'];
            this.linkedOrgCodes = info.LinkedAgencies?.map(x => x.AgencyID);
            this.cdsVirtualGroups = info.CDSVirtualGroups;
          }
          this.currentUserId = info["Id"];
          this.currentUserName = info["UserId"];
          this.loginService.nextUserInfo(info);
          resolve();
        },
        error: (err:any) => {
          reject();
        }
      });
    });
  }

  canView(virtualGroupRelation: number): boolean {
    return (virtualGroupRelation ?? 0) == VirtualGroupRelation.Guest
      || (virtualGroupRelation ?? 0) == VirtualGroupRelation.MyVirtualGroup;
  }

  canEdit(virtualGroupRelation: number): boolean {
    return (virtualGroupRelation ?? 0) == VirtualGroupRelation.MyVirtualGroup;
  }

  // This method is called once and a list of claims is stored in the claims property
  initializeClaims() {
    this.clearClaims();
    return new Promise<void>((resolve, reject) => {
      // Call API to retrieve the list of actions this user is permitted to perform.
      this.loginService.getUserClaims().subscribe({
        next: (claims: any[]) => {
          this.claims = claims;
          resolve()
        },
        error: (err:any) => {
          console.log(err.error);
          reject();
        }
      });
    });
  }

  public getAuthTokenByRefreshToken(refreshToken: string) {
    let data = `refresh_token=${refreshToken}&grant_type=refresh_token&client_id=${environment.authTokenClientId}`;
    return this.http.post(`${this.apiRoot}/token`, data, {
      headers:
        new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        })
    });
  }
}
